import type { AssessmentStandardOption } from '../../model/assessments/interfaces'
import type { ApiCallbacks, ApiResponse } from '@shared/api'
import type { DisciplineCode } from '@brighterly/lib-core-types/student-assessment/types'
import type { Grade } from '@brighterly/lib-core-types/kid/enums'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface AssessmentStandardsApiOptions extends ApiCallbacks<AssessmentStandardOption[]> {
  disciplineCode: DisciplineCode
  grade: Grade
}

/**
 * `GET /admin-api/assessment-standards?discipline_code=…&grade=…` — the picker's catalogue.
 *
 * Both filters are required, and there is no catalogue-version parameter: the server always
 * answers from the newest catalogue. Unpaginated — the widest grade is well under 200 rows.
 *
 * Deliberately touches no loading store: this is a fetch behind a dropdown, and the global
 * spinner would flash a full-page loader every time an author opens a picker.
 */
export const useAssessmentStandardsApi = async (
  options: AssessmentStandardsApiOptions,
): Promise<AssessmentStandardOption[] | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.assessment-standards.index', {
    discipline_code: options.disciplineCode,
    grade: options.grade,
  })

  options.onStart?.()

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return null
    }

    const standards = data.data?.standards

    if (!Array.isArray(standards)) {
      const msg = 'Unexpected error. The standards are missing from the response.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    options.onSuccess?.(standards)
    return standards
  }
  catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)
    return null
  }
  finally {
    options.onFinish?.()
  }
}
